import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import { installChunkReload } from './chunkReload';
import { installFloatingFit } from './utils/fitFloating';
import { installModalScrollLock } from './utils/modalScrollLock';

// Before anything can lazy-load: a tab left open across a deploy reloads
// onto the new build instead of failing. See chunkReload.ts.
installChunkReload();
// Keeps every [data-fit-viewport] menu on screen (utils/fitFloating.ts).
installFloatingFit();
installModalScrollLock();

// Benign Chrome noise: the browser throws "ResizeObserver loop completed
// with undelivered notifications" whenever a ResizeObserver callback
// (charts, resizable panels, autosize fields — several in this app)
// triggers another layout pass in the same frame. Harmless and invisible in
// a prod build (no error overlay there).
//
// A window 'error' listener here can't stop it: CRA's dev-overlay client
// script registers its own 'error' listener before this file ever runs, and
// browsers call listeners in registration order, so by the time ours fires
// the overlay has already been shown — stopImmediatePropagation() is too
// late. Debouncing the callback via requestAnimationFrame instead prevents
// the same-frame re-entrant resize that causes the loop in the first place,
// so the error never fires at all.
if (typeof window !== 'undefined' && 'ResizeObserver' in window) {
  const OriginalResizeObserver = window.ResizeObserver;
  window.ResizeObserver = class extends OriginalResizeObserver {
    constructor(callback: ResizeObserverCallback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => callback(entries, observer));
      });
    }
  };
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// Registers src/service-worker.ts (production build only). Required for
// `beforeinstallprompt` to fire at all — see serviceWorkerRegistration.ts.
serviceWorkerRegistration.register();
